import { useDispatch } from "react-redux";
import { GetDeviceLogPageable } from "../../../../reduxToolkit/Devices/DevicesApi";
import { logMessage } from "../../../../enums/logMessage";
import TableFunction from "../../../../components/table/TableFunction";
import CustomTh from "../../../../components/table/CustomTh";
import CustomTd from "../../../../components/table/CustomTd";
import {
    DATE_TIME_FORMAT_NAME_MODEL,
    EMPTY_VALUES,
    errorMsg,
    getDateFormat,
    getTimeFormat
} from "../../../../Apis/functions";
import CustomChip from "../../../../components/CustomChip";
import { colors } from "../../../../enums/colors";
import {
    IconClockTable,
    IconScheduleTable
} from "../../../../components/buttons/IconButtons";
import { useEffect, useState } from "react";

/**
 * Author: Arnulfo Ferro Zapata
 * Job Title: Fullstack
 * Email: azpata@ibl.mx
 * Created At: 06 de 05 de 2026
 */

/**
 * Custom hook used to manage device log modal data and rendering.
 *
 * This hook handles:
 * - Device log retrieval.
 * - Pagination integration.
 * - Table rendering.
 * - Device log categorization.
 * - Message parsing and translation.
 * - Dynamic log description generation.
 *
 * @function DeviceLogModalApp
 *
 * @param {Object} props - Hook parameters.
 * @param {Object} props.data - Device information object.
 *
 * @returns {Object} Hook response object.
 * @returns {Object} returns.pagination - Pagination configuration.
 * @returns {Function} returns.t - Translation function.
 * @returns {string} returns.tableId - Unique table identifier.
 * @returns {Array<JSX.Element>} returns.thead - Table header elements.
 * @returns {Object} returns.tbody - Table body data and rendered rows.
 */
export const DeviceLogModalApp = ({ data }) => {

    const uuid = data?.id;
    const dispatch = useDispatch();
    const [modified, setModified] = useState(false);
    const tableId = "tbl_dev_device_log";
    const [searchResults, setSearchResults] = useState(null);

    /**
     * Available device log categories.
     *
     * @readonly
     * @enum {string}
     */
    const DEVICE_LOG_CATEGORY = {
        CONNECTIVITY: 'connectivity',
        INFORMATION: 'information',
    };

    /**
     * Device log category mapping by message code.
     *
     * @readonly
     * @type {Object<string, string>}
     */
    const DEVICE_LOG_CATEGORY_BY_MESSAGE_CODE = {

        /**
         * Connectivity logs.
         */
        '40': DEVICE_LOG_CATEGORY.CONNECTIVITY,
        '1000': DEVICE_LOG_CATEGORY.CONNECTIVITY,
        '1001': DEVICE_LOG_CATEGORY.CONNECTIVITY,

        /**
         * Informational logs.
         */
        '50': DEVICE_LOG_CATEGORY.INFORMATION,
        '51': DEVICE_LOG_CATEGORY.INFORMATION,
        '60': DEVICE_LOG_CATEGORY.INFORMATION,
    };

    /**
     * Returns log category based on message code.
     *
     * @param {string} message - Raw device log message.
     *
     * @returns {string} Device log category.
     */
    const getDeviceLogCategory = (message) => {

        if (!message) {
            return DEVICE_LOG_CATEGORY.INFORMATION;
        }

        const parts = message.split(',');
        const messageCode = parts[0]?.trim();

        return (
            DEVICE_LOG_CATEGORY_BY_MESSAGE_CODE[messageCode] ||
            DEVICE_LOG_CATEGORY.INFORMATION
        );
    };

    /**
     * Device log table configuration.
     *
     * @readonly
     * @type {Object}
     */
    const DEVICE_LOG_GET_DATA = {
        DEVICE: {
            name: "DEVICE",

            /**
             * Builds pageable request body.
             *
             * @param {Object} params - Pagination parameters.
             * @param {number} params.page - Current page number.
             * @param {number} params.rowsPerPage - Rows per page.
             * @param {number|string} params.id - Device identifier.
             *
             * @returns {Object} Request body.
             */
            buildBody: ({ page, rowsPerPage, id }) => ({
                id,
                body: {
                    order: true,
                    page,
                    size: rowsPerPage,
                    sortBy: 'id'
                }
            }),

            /**
             * Redux action used to retrieve device logs.
             */
            action: GetDeviceLogPageable,

            /**
             * Empty table translation key.
             */
            txtNotInformation: 'you_dont_have_device_log_yet'
        },
    };

    /**
     * Generates table header elements.
     *
     * @returns {Array<JSX.Element>} Table header.
     */
    const getThead = () => {

        let header = [
            <CustomTh
                tableId={tableId}
                className='first_head'
            >
                {t('date')}
            </CustomTh>
        ];

        header.push(
            <CustomTh tableId={tableId}>
                {t('message')}
            </CustomTh>
        );

        header.push(
            <CustomTh tableId={tableId}>
                {t('type')}
            </CustomTh>
        );

        return header;
    };

    /**
     * Generates table body elements.
     *
     * @returns {Object} Table body configuration.
     */
    const getTbody = () => {

        if (
            !searchResults ||
            searchResults?.content?.length === 0
        ) {
            return {
                body: [],
                data: []
            };
        }

        /**
         * Rendered table rows.
         */
        let body = searchResults?.content?.map((item) => {

            return (
                <>
                    <CustomTd tableId={tableId}>

                        <div className={"d-flex"}>
                            <IconScheduleTable
                                className={
                                    "mr-8px mb-1 icon-color-general-950"
                                }
                            />

                            {getDateFormat(
                                item?.createdAt,
                                false,
                                DATE_TIME_FORMAT_NAME_MODEL.shortMonth
                            )}
                        </div>

                        <div className={"d-flex"}>
                            <IconClockTable
                                className={
                                    "mr-8px mb-1 icon-color-general-950"
                                }
                            />

                            {getTimeFormat(item?.createdAt)}
                        </div>

                    </CustomTd>

                    <CustomTd tableId={tableId}>
                        {getMessageDescription(item)}
                    </CustomTd>

                    <CustomTd tableId={tableId}>
                        <CustomChip
                            background={colors.secondary100}
                            legend={
                                t(getDeviceLogCategory(item?.message)) ||
                                EMPTY_VALUES.DEFAULT
                            }
                            fontColor={colors.secondary150}
                            borderColor={colors.secondary100}
                        />
                    </CustomTd>
                </>
            );
        });

        return {
            body,
            data: searchResults?.content
        };
    };

    /**
     * Loads initial device log data.
     */
    useEffect(() => {
        getData();
    }, []);

    /**
     * Refreshes device logs when modified state changes.
     */
    useEffect(() => {

        if (modified) {

            setModified(false);
            getData();
        }

    }, [modified]);

    /**
     * Active table data configuration.
     */
    const getDataBuild = DEVICE_LOG_GET_DATA.DEVICE;

    /**
     * Retrieves paginated device logs.
     *
     * @param {number} [page] - Current page number.
     */
    const getData = page => {

        const body = getDataBuild.buildBody({
            page,
            rowsPerPage,
            id: uuid
        });

        dispatch(getDataBuild.action(body)).then(
            ({
                 payload: {
                     data: {
                         data,
                         success,
                         code
                     }
                 }
             }) => {

                if (success === true) {

                    setSearchResults(data);

                } else {

                    errorMsg(code);
                }
            }
        );
    };

    /**
     * Shared table utility hook.
     */
    const {
        t,
        thead,
        tbody,
        pagination,
        rowsPerPage
    } = TableFunction({
        data: searchResults,
        getThead,
        getTbody,
        labelRowsPerPage: 'logs_per_page',
        hasContainer: true,
        fixedPagination: false,
        getData
    });

    /**
     * Generates translated log description based on message code.
     *
     * @param {Object} item - Device log item.
     * @param {string} item.message - Raw device log message.
     *
     * @returns {string} Translated log description.
     */
    const getMessageDescription = (item) => {

        let actionDescription = '';

        /**
         * Parses log message details.
         */
        const parts = item.message.split(',');
        const messageCode = parts[0].trim();
        const firstDetail = parts[1]?.trim() || '';
        const secondDetail = parts[2]?.trim() || '';

        switch (messageCode) {

            case '1':
                actionDescription = `${t('APP_CLOSED')}`;
                break;

            case '2':
                actionDescription = `${t('APP_STARTED')}`;
                break;

            case '40':
                actionDescription =
                    `${t('new_IP')} ${firstDetail} ` +
                    `${t('or_MAC')} ${secondDetail} ` +
                    `${t('has_changed_verify')}`;
                break;

            case '50':
                actionDescription =
                    `${t('user_with_id:')} ${firstDetail} ` +
                    `${t('try_access')}`;
                break;

            case '51':
                actionDescription =
                    `${t('user_with_id:')} ${firstDetail} ` +
                    `${t('access_waiting')}`;
                break;

            case '60':
                actionDescription =
                    `${t('id:')} ${firstDetail} ` +
                    `${t('access_method_invalid')} ${secondDetail}`;
                break;

            case '101':
                actionDescription =
                    `${t('FAR_ERROR_CONVERTING_SELFIE')} ${firstDetail}`;
                break;

            case '102':
                actionDescription =
                    `${t('FAR_ERROR_CONVERTING_SELFIE_TO_IMAGE')} ${firstDetail}`;
                break;

            case '103':
                actionDescription =
                    `${t('FAR_ERROR_CONVERTING_SELFIE_TO_BMP')} ${firstDetail}`;
                break;

            case '108':
                actionDescription =
                    `${t('FAR_ERROR_VERY_HEAVY_IMAGE', {
                        s: firstDetail
                    })}`;
                break;

            case '109':
                actionDescription =
                    `${t('FAR_ERROR_DECODE_BOUNDS', {
                        s: firstDetail
                    })}`;
                break;

            case '110':
                actionDescription =
                    `${t('FAR_ERROR_COMPRESS_IMAGE', {
                        s: firstDetail
                    })}`;
                break;

            case '111':
                actionDescription =
                    `${t('FAR_ERROR_OUT_OF_MEMORY', {
                        s: firstDetail
                    })}`;
                break;

            case '1000':
                actionDescription =
                    `${t('error_syncing_device')}`;
                break;

            case '1001':
                actionDescription =
                    `${t('device_synced_successfully')}`;
                break;

            case '1002':
                actionDescription =
                    `${t('INFORMATION_SYNCHRONIZATION_INIT')}`;
                break;

            default:

                if (logMessage[messageCode]) {

                    actionDescription =
                        t(logMessage[messageCode]);

                } else {

                    actionDescription = 'Message not found';
                }

                break;
        }

        return actionDescription;
    };

    return {
        pagination,
        t,
        tableId,
        thead,
        tbody
    };
};
